import React, { useCallback, useState } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import type { FormContext } from "@custom-types/form-types.ts";
import { usePersistentForm } from "@hooks/usePersistentForm";
import type { Vessel } from "@services/vessel/types";
import type { VesselCallResponse } from "@services/vesselCall/types";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { z } from "zod";
import { getNotifyOptionRequiredMessage } from "./flow/utils";
import { getMissingNotifyRolesMessage } from "./form/utils";
import { useSubmitPortService } from "./hooks/useSubmitPortService";
import {
  getPortServicesFormSchema,
  type PortServicesFormData,
} from "./schema/portServicesFormSchema";
import { defaultValues, usePortServicesFormStore } from "./store/usePortServicesForm.store";

interface OutletContextType {
  vesselCallDetails: VesselCallResponse;
  vesselRegisterDetails?: Vessel | null;
  isThirdShippingAgent?: boolean;
  isOwnersMatter?: boolean;
}

export type PortServicesOutletContext = FormContext<PortServicesFormData> & {
  resetPersistedForm: () => void;
  handleSubmit: () => void;
  cancel: () => void;
  expectedArrivalLocation?: string;
  currentVesselImoNumber?: string;
  vesselId?: string;
  vesselCallId?: string;
  vesselCallNumber?: string;
  vesselCallStatus?: string;
  selectedPortCode?: string;
  isDibbaVesselCall?: boolean;
  notifyOption: string[];
  setNotifyOption: (options: string[]) => void;
  errors: {
    notifyOption?: string;
  };
  setErrors: (errors: { notifyOption?: string }) => void;
  submitError?: string | null;
  isSubmitting: boolean;
  isThirdShippingAgent?: boolean;
  isOwnersMatter?: boolean;
};

const LANG_BASE = "vessel-call.port-services";
const PORT_OF_DIBBA_REFERENCE_CODE = "POD";
const SHIP_TO_SHIP_CATEGORY = "Ship to Ship";
const BUNKER_BARGE_CATEGORIES = ["Bunkering", "De-bunkering", "De-sloping"];

const getNotifySchema = () =>
  z
    .object({
      notifyOption: z.array(z.string()).min(1, { message: getNotifyOptionRequiredMessage() }),
      serviceSubType: z.string().optional(),
      serviceType: z.string().optional(),
      serviceCategory: z.string(),
    })
    .superRefine((data, ctx) => {
      const missingNotifyRolesMessage = getMissingNotifyRolesMessage(
        {
          serviceCategory: data.serviceCategory,
          serviceType: data.serviceType,
          serviceSubType: data.serviceSubType,
        },
        data.notifyOption,
      );

      if (missingNotifyRolesMessage) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: missingNotifyRolesMessage,
          path: ["notifyOption"],
        });
      }
    });

const PortServicesContainer: React.FC = () => {
  const { vesselCallDetails, vesselRegisterDetails, isThirdShippingAgent, isOwnersMatter } =
    useOutletContext<OutletContextType>();
  const { t } = useTranslation();
  const notifySchema = getNotifySchema();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const [notifyOption, setNotifyOption] = useState<string[]>([]);
  const [errors, setErrors] = useState<{
    notifyOption?: string;
  }>({});

  const clearUploadStore = useUploadStore((state) => state.clearAll);
  const { setField, setAll, resetForm } = usePortServicesFormStore();
  const currentVesselImoNumber =
    vesselRegisterDetails?.vesselImoNumber?.toString() ??
    vesselCallDetails?.generalDeclaration?.imoNumber?.toString();

  const isDibbaVesselCall =
    vesselCallDetails?.selectPortReferenceCode === PORT_OF_DIBBA_REFERENCE_CODE;

  const initialStoreValues = usePortServicesFormStore.getState();
  const baseFormValues: PortServicesFormData = {
    serviceCategory: initialStoreValues.serviceCategory ?? "",
    serviceType: initialStoreValues.serviceType ?? "",
    serviceSubType: initialStoreValues.serviceSubType ?? "",
    otherVesselImoNumber: initialStoreValues.otherVesselImoNumber ?? "",
    otherVesselId: initialStoreValues.otherVesselId ?? "",
    otherVesselName: initialStoreValues.otherVesselName ?? "",
    otherVesselCallId: initialStoreValues.otherVesselCallId ?? "",
    otherVesselCallNumber: initialStoreValues.otherVesselCallNumber ?? "",
    sireReportIssueDate: initialStoreValues.sireReportIssueDate ?? null,
    hoseInspectionDate: initialStoreValues.hoseInspectionDate ?? null,
    sireReportDocuments: initialStoreValues.sireReportDocuments ?? [],
    billOfLadingDocuments: initialStoreValues.billOfLadingDocuments ?? [],
    cargoManifestDocuments: initialStoreValues.cargoManifestDocuments ?? [],
    certificateOfOriginDocuments: initialStoreValues.certificateOfOriginDocuments ?? [],
    hoseCertificateDocuments: initialStoreValues.hoseCertificateDocuments ?? [],
    otherDocuments: initialStoreValues.otherDocuments ?? [],
    direction: initialStoreValues.direction ?? "",
    numberOfPackagesHandled: initialStoreValues.numberOfPackagesHandled ?? "",
    additionalResources: initialStoreValues.additionalResources ?? [],
    receivingVesselImoNumber: initialStoreValues.receivingVesselImoNumber ?? "",
    receivingVesselId: initialStoreValues.receivingVesselId ?? "",
    receivingVesselName: initialStoreValues.receivingVesselName ?? "",
    receivingVesselCallId: initialStoreValues.receivingVesselCallId ?? "",
    receivingVesselCallNumber: initialStoreValues.receivingVesselCallNumber ?? "",
    receivingVesselLookupPending: initialStoreValues.receivingVesselLookupPending ?? false,
    customsDocuments: initialStoreValues.customsDocuments ?? [],
    wasteResiduesForDischargeFacility: initialStoreValues.wasteResiduesForDischargeFacility ?? [],
    rts: initialStoreValues.rts ?? "",
    description: initialStoreValues.description ?? "",
    remarks: initialStoreValues.remarks ?? "",
    attachmentDocuments: initialStoreValues.attachmentDocuments ?? [],
    garbageDocument: initialStoreValues.garbageDocument ?? [],
    slopsDocument: initialStoreValues.slopsDocument ?? [],
    sludgeDocument: initialStoreValues.sludgeDocument ?? [],
    debunkeringDocument: initialStoreValues.debunkeringDocument ?? [],
    newChatMessage: initialStoreValues.newChatMessage ?? "",
    bunkeringType: initialStoreValues.bunkeringType ?? "",
    bunkeringCompanyName: initialStoreValues.bunkeringCompanyName ?? "",
    bunkerBargeIMO: initialStoreValues.bunkerBargeIMO ?? "",
    bunkerBargeName: initialStoreValues.bunkerBargeName ?? "",
    bunkerBargeVesselCallId: initialStoreValues.bunkerBargeVesselCallId ?? "",
    bunkerBargeLookupPending: initialStoreValues.bunkerBargeLookupPending ?? false,
    bunkerBargeLookupErrorMessageKey: initialStoreValues.bunkerBargeLookupErrorMessageKey ?? "",
    deSlopingType: initialStoreValues.deSlopingType ?? "",
    deSlopingCompanyName: initialStoreValues.deSlopingCompanyName ?? "",
  };
  const form = usePersistentForm<PortServicesFormData>({
    schema: getPortServicesFormSchema(t, {
      currentVesselImoNumber,
    }),
    defaultValues: defaultValues,
    storeValues: baseFormValues,
    //@ts-expect-error To fix type error
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: "request-port-services-form",
  });
  const { clear, clearErrors, watch, setError } = form;

  const updateFormData: PortServicesOutletContext["updateFormData"] = useCallback(
    (key, value) => {
      const typedKey = key as keyof PortServicesFormData;
      const typedValue = value as PortServicesFormData[keyof PortServicesFormData];
      //@ts-expect-error To fix type error
      setField(typedKey, typedValue);
    },
    [setField],
  );
  const uploadStore = useUploadStore.getState();

  const clearForm = useCallback(() => {
    resetForm();
    clear();
  }, [resetForm, clear, setField, uploadStore]);

  const {
    onSubmit,
    error: submitError,
    isSubmitting,
  } = useSubmitPortService({
    getFormValues: () => form.getValues() as PortServicesFormData,
    resetForm: clearForm,
    vesselCallId: vesselCallDetails?.id || "",
    notifyList: notifyOption,
    vesselCallStatus: vesselCallDetails?.status || "",
    isThirdShippingAgent,
    isOwnersMatter,
  });

  const onValid = async () => {
    // this only runs if zod validation passed
    const serviceCategory = form.getValues("serviceCategory");
    const otherVesselImoNumber = form.getValues("otherVesselImoNumber")?.trim();
    const otherVesselCallId = form.getValues("otherVesselCallId")?.trim();

    if (serviceCategory === SHIP_TO_SHIP_CATEGORY && otherVesselImoNumber && !otherVesselCallId) {
      form.setError("otherVesselImoNumber", {
        type: "manual",
        message: t(`${LANG_BASE}.fields.other-vessel-validation-required`),
      });

      notify({
        title: t(`${LANG_BASE}.notifications.service-added-error-title`),
        description: t(`${LANG_BASE}.notifications.service-added-error-description`),
        type: "error",
      });

      return;
    }

    const result = notifySchema.safeParse({
      serviceCategory,
      serviceType: form.getValues("serviceType"),
      notifyOption: notifyOption,
      serviceSubType: form.getValues("serviceSubType"),
    });

    if (!result.success) {
      const fieldErrors: typeof errors = {};
      for (const issue of result.error.issues) {
        const pathKey = issue.path[0] as keyof typeof fieldErrors;
        if (!pathKey) continue;
        fieldErrors[pathKey] = issue.message;
      }
      setErrors(fieldErrors);

      notify({
        title: t(`${LANG_BASE}.notifications.service-added-error-title`),
        description: t(`${LANG_BASE}.notifications.service-added-error-description`),
        type: "error",
      });

      return;
    }

    await onSubmit();
  };

  const onInvalid = () => {
    // this runs when zod validation fails

    const result = notifySchema.safeParse({
      serviceCategory: form.getValues("serviceCategory"),
      serviceType: form.getValues("serviceType"),
      notifyOption: notifyOption,
      serviceSubType: form.getValues("serviceSubType"),
    });

    if (!result.success) {
      const fieldErrors: typeof errors = {};
      for (const issue of result.error.issues) {
        const pathKey = issue.path[0] as keyof typeof fieldErrors;
        if (!pathKey) continue;
        fieldErrors[pathKey] = issue.message;
      }
      setErrors(fieldErrors);
    }

    notify({
      title: t(`${LANG_BASE}.notifications.service-added-error-title`),
      description: t(`${LANG_BASE}.notifications.service-added-error-description`),
      type: "error",
    });
  };

  const handleSubmit = () => {
    const bunkerBargeIMO = form.getValues("bunkerBargeIMO")?.toString().trim();
    const bunkerBargeName = form.getValues("bunkerBargeName")?.trim();
    const isBunkerBargeLookupPending = form.getValues("bunkerBargeLookupPending") === true;
    const receivingVesselImoNumber = form.getValues("receivingVesselImoNumber")?.trim();
    const receivingVesselCallId = form.getValues("receivingVesselCallId")?.trim();
    const isReceivingVesselLookupPending = form.getValues("receivingVesselLookupPending") === true;
    const isBunkerBargeService = BUNKER_BARGE_CATEGORIES.includes(
      form.getValues("serviceCategory"),
    );

    if (isBunkerBargeService && bunkerBargeIMO && !bunkerBargeName) {
      if (isBunkerBargeLookupPending) {
        notify({
          title: t(`${LANG_BASE}.notifications.service-added-error-title`),
          description: t(`${LANG_BASE}.notifications.service-added-error-description`),
          type: "error",
        });

        return;
      }

      form.setValue("bunkerBargeLookupPending", false);
      setField("bunkerBargeLookupPending", false);
    }

    if (
      form.getValues("serviceCategory") === "Ship Chandling" &&
      receivingVesselImoNumber &&
      !receivingVesselCallId &&
      isReceivingVesselLookupPending
    ) {
      form.setValue("receivingVesselLookupPending", false);
      setField("receivingVesselLookupPending", false);
    }

    form.handleSubmit(onValid, onInvalid)();
  };

  const cancel = () => {
    clearForm();
    navigate(
      `/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}/${vesselCallDetails?.id}/${paths.vesselCall.portServices}`,
    );
    clearUploadStore();
  };

  const outletCtx: PortServicesOutletContext = {
    formData: form,
    updateFormData,
    resetPersistedForm: clearForm,
    handleSubmit,
    cancel,
    clearErrors: clearErrors,
    setError,
    watch,
    expectedArrivalLocation: vesselCallDetails?.expectedArrivalLocation,
    currentVesselImoNumber,
    vesselId: vesselCallDetails?.vesselId,
    vesselCallId: vesselCallDetails?.id,
    vesselCallNumber: vesselCallDetails?.vcn,
    vesselCallStatus: vesselCallDetails?.status,
    selectedPortCode: vesselCallDetails?.selectPortReferenceCode,
    isDibbaVesselCall,
    notifyOption,
    setNotifyOption,
    errors,
    setErrors,
    submitError,
    isSubmitting,
    isThirdShippingAgent,
    isOwnersMatter,
  };

  return (
    <div className="mt-4 px-8 flex flex-col gap-6">
      <Outlet context={outletCtx} />
    </div>
  );
};

export default PortServicesContainer;
